import {
  Component,
  OnInit,
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  NgZone,
  ChangeDetectorRef,
  effect,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { SharedModule } from '../../shared/shared.module';

@Component({
  standalone: true,
  selector: 'app-progress-bar',
  templateUrl: './progress-bar.component.html',
  styleUrls: ['./progress-bar.component.scss'],
  imports: [CommonModule, TooltipModule, SharedModule],
})
export class ProgressBarComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  // Signal (não campo comum) porque esse @Input troca ao vivo (switch na
  // sidebar de slick-detail) e precisa disparar o MESMO effect() que já
  // sincroniza marker/barra com currentTime — um campo comum não é rastreado
  // por effect(), então a troca só refletiria no próximo tick de 'timeupdate'
  // (ou nunca, se o vídeo estiver pausado), deixando marker e barra
  // dessincronizados até dar reload na página.
  private showProgressBarSignal = signal(false);
  @Input()
  set showProgressBar(value: boolean) {
    this.showProgressBarSignal.set(value);
  }
  get showProgressBar(): boolean {
    return this.showProgressBarSignal();
  }

  @Input() videoDurationSeconds: number = 0;
  @Input() progressBarColor: string = '#FF6347';
  @Input() chapters: { title: string; time: string; product_sku?: string }[] = [];
  @Input() chaptersEnabled: boolean = false;
  @Input() isFullscreen: boolean = false;
  @Input() isMobile: boolean = false;
  @Input() withLeftPadding: boolean = true;
  @Input() speedOptions: number[] = [1, 1.5, 2];

  private _videoElement?: HTMLVideoElement;
  @Input()
  set videoElement(el: HTMLVideoElement | undefined) {
    this._videoElement = el;
    this.applyCurrentSpeed();
  }
  get videoElement(): HTMLVideoElement | undefined {
    return this._videoElement;
  }

  currentSpeed: number = 1;

  private currentTimeSignal = signal(0);
  @Input()
  set currentTime(value: number) {
    this.currentTimeSignal.set(value);
  }
  get currentTime(): number {
    return this.currentTimeSignal();
  }

  @Output() seekTo = new EventEmitter<number>();
  @Output() dragStarted = new EventEmitter<void>();
  @Output() dragEnded = new EventEmitter<{ wasPlaying: boolean }>();
  @Output() tooltipChange = new EventEmitter<{ time: string; chapterTitle: string; duration: string; show: boolean }>();

  @ViewChild('progressContainerWrapper') progressContainerWrapper?: ElementRef<HTMLDivElement>;
  @ViewChild('progressContainer') progressContainer?: ElementRef;
  @ViewChild('originalProgress') originalProgress?: ElementRef;
  @ViewChild('parallelProgress') parallelProgress?: ElementRef;
  @ViewChild('marker') marker?: ElementRef<HTMLDivElement>;

  isDragging: boolean = false;
  displayedTime: string = '';
  displayedChapterTitle: string = '';

  // Tooltip flutuante que segue o mouse ao passar por cima de um trecho entre
  // dois capítulos (diferente do tooltipChange, que é o tooltip do drag).
  segmentTooltipVisible: boolean = false;
  segmentTooltipText: string = '';
  segmentTooltipX: number = 0;
  segmentTooltipY: number = 0;

  private resizeObserver?: ResizeObserver;
  // Largura (em px) do "furo" cortado no mask em cada fronteira de capítulo.
  private readonly chapterMaskHoleWidthPx = 5;

  private initPlayPause: boolean = false;
  private animationFrameId?: number;
  private listenersAdded: boolean = false;
  private boundOnDragging: (e: MouseEvent | TouchEvent) => void;
  private boundOnDragEnd: (e: MouseEvent | TouchEvent) => void;
  private spriteListeners: {
    touchMoveListener?: (event: TouchEvent) => void;
    touchEndListener?: () => void;
    mouseMoveListener?: (event: MouseEvent) => void;
    mouseLeaveListener?: () => void;
  } = {};
  private hoverListeners: {
    mouseEnterListener?: (event: MouseEvent) => void;
    mouseMoveListener?: (event: MouseEvent) => void;
    mouseLeaveListener?: () => void;
  } = {};

  get formattedDuration(): string {
    return this.formatDuration2(this.videoDurationSeconds);
  }

  constructor(
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    this.boundOnDragging = this.onDragging.bind(this);
    this.boundOnDragEnd = this.onDragEnd.bind(this);

    effect(() => {
      const time = this.currentTimeSignal();
      this.showProgressBarSignal(); // dependência: reroda a sync quando o modo trocar, não só o tempo
      if (!this.isDragging) {
        this.updateBarFromCurrentTime(time);
      }
    });
  }

  ngOnInit(): void {
    // Initialization if needed
  }

  ngAfterViewInit(): void {
    // Recalcula os furos do mask quando a largura do container muda (resize
    // de janela, entrar/sair de fullscreen, troca de dispositivo mobile/desktop)
    // — sem isso, o mask calculado em px ficaria desatualizado.
    if (this.progressContainer?.nativeElement && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateBarFromCurrentTime(this.currentTime);
      });
      this.resizeObserver.observe(this.progressContainer.nativeElement);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // chapters/chaptersEnabled não são signals (diferente de showProgressBar/currentTime),
    // então o effect() do construtor não reage a eles — sem isso, adicionar um
    // capítulo novo na sidebar só refletia na barra depois de um seek/drag
    // (o que dispara updateBarFromCurrentTime por outro motivo) ou de dar
    // reload na página.
    if (changes['chapters'] || changes['chaptersEnabled']) {
      this.updateBarFromCurrentTime(this.currentTime);
    }
  }

  ngOnDestroy(): void {
    this.removeAllListeners();
    this.resizeObserver?.disconnect();
  }

  private applyCurrentSpeed(): void {
    if (this._videoElement && this.currentSpeed !== 1) {
      this._videoElement.playbackRate = this.currentSpeed;
    }
  }

  // Clica e já troca pra próxima velocidade da lista (sem abrir menu),
  // voltando pra primeira opção ao passar da última.
  cycleSpeed(event: Event): void {
    event.stopPropagation();
    const currentIndex = this.speedOptions.indexOf(this.currentSpeed);
    const nextIndex = (currentIndex + 1) % this.speedOptions.length;
    this.currentSpeed = this.speedOptions[nextIndex];
    if (this._videoElement) {
      this._videoElement.playbackRate = this.currentSpeed;
    }
  }

  private updateBarFromCurrentTime(time: number): void {
    if (!this.progressContainer) {
      return;
    }

    const containerWidth = this.progressContainer.nativeElement.getBoundingClientRect().width;
    let progressPercentage: number;

    if (this.showProgressBar) {
      if (time < this.videoDurationSeconds * 0.3) {
        progressPercentage = (time / (this.videoDurationSeconds * 0.3)) * 70;
      } else {
        progressPercentage = 70 + ((time - this.videoDurationSeconds * 0.3) / (this.videoDurationSeconds * 0.7)) * 30;
      }
      if (this.parallelProgress) {
        this.parallelProgress.nativeElement.style.width = `${progressPercentage}%`;
      }
    } else {
      progressPercentage = (time / this.videoDurationSeconds) * 100;
      if (this.originalProgress) {
        this.originalProgress.nativeElement.style.width = `${progressPercentage}%`;
      }
    }

    if (this.marker) {
      const markerPosition = (progressPercentage / 100) * containerWidth;
      this.marker.nativeElement.style.left = `${markerPosition - 7}px`;
    }

    this.applyChapterMasks(containerWidth, (progressPercentage / 100) * containerWidth);
  }

  onDragStart(event: MouseEvent | TouchEvent): void {
    this.isDragging = true;
    this.initPlayPause = !(this.videoElement?.paused ?? true);
    // Pausa de verdade aqui (não só via efeito colateral do seek) — sem isso,
    // se o usuário para de mover o dedo/mouse sem soltar o marker, o vídeo
    // retoma sozinho assim que o seek em andamento termina.
    if (this.initPlayPause) {
      this.videoElement?.pause();
    }
    this.dragStarted.emit();

    if (this.progressContainer && !this.listenersAdded) {
      this.progressContainer.nativeElement.addEventListener('mousemove', this.boundOnDragging);
      this.progressContainer.nativeElement.addEventListener('mouseup', this.boundOnDragEnd);
      this.progressContainer.nativeElement.addEventListener('touchmove', this.boundOnDragging);
      this.progressContainer.nativeElement.addEventListener('touchend', this.boundOnDragEnd);
      this.listenersAdded = true;
      document.addEventListener('mousemove', this.boundOnDragging);
      document.addEventListener('mouseup', this.boundOnDragEnd);
      document.addEventListener('touchmove', this.boundOnDragging);
      document.addEventListener('touchend', this.boundOnDragEnd);
    }
  }

  onDragging(event: MouseEvent | TouchEvent): void {
    if (!this.isDragging) return;

    const progressContainer = this.progressContainer?.nativeElement;
    if (!progressContainer) return;

    const rect = progressContainer.getBoundingClientRect();
    let clientX: number | null = this.getClientXFromEvent(event);
    if (clientX === null) return;

    const containerWidth = rect.width;
    const clickX = Math.max(0, Math.min(clientX - rect.left, containerWidth));
    const visualClickPercentage = clickX / containerWidth;

    let seekPercentage: number;
    if (this.showProgressBar) {
      if (visualClickPercentage < 0.7) {
        seekPercentage = (visualClickPercentage / 0.7) * 0.3;
      } else {
        seekPercentage = 0.3 + ((visualClickPercentage - 0.7) / 0.3) * 0.7;
      }
    } else {
      seekPercentage = visualClickPercentage;
    }

    let progressPercentage;
    if (this.showProgressBar) {
      if (seekPercentage < 0.3) {
        progressPercentage = (seekPercentage / 0.3) * 70;
      } else {
        progressPercentage = 70 + ((seekPercentage - 0.3) / 0.7) * 30;
      }
      if (this.parallelProgress) {
        this.parallelProgress.nativeElement.style.width = `${progressPercentage}%`;
      }
    } else {
      progressPercentage = seekPercentage * 100;
      if (this.originalProgress) {
        this.originalProgress.nativeElement.style.width = `${progressPercentage}%`;
      }
    }

    const seekTime = seekPercentage * this.videoDurationSeconds;
    this.seekTo.emit(seekTime);

    const markerX = visualClickPercentage * containerWidth;
    if (this.marker?.nativeElement) {
      this.marker.nativeElement.style.left = `${markerX - 7}px`;
    }

    this.applyChapterMasks(containerWidth, (progressPercentage / 100) * containerWidth);

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    this.animationFrameId = requestAnimationFrame(() => {
      if (clientX !== null) {
        this.updateThumbnail(clientX); // Atualizar posição do tooltip/timeDisplay
      }
      this.updateDisplayedTime(seekTime);
      if (this.progressContainer) {
        if (this.isMobile) {
          this.progressContainer.nativeElement.classList.add('ss-g-progress-container-expanded');
        }
      }
    });
  }

  onDragEnd(event: MouseEvent | TouchEvent): void {
    if (this.isDragging) {
      this.isDragging = false;

      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = undefined;
      }

      this.updateProgress(event);

      if (this.progressContainer) {
        if (this.isMobile) {
          this.progressContainer.nativeElement.classList.remove('ss-g-progress-container-expanded');
        }
        this.progressContainer.nativeElement.removeEventListener('mousemove', this.boundOnDragging);
        this.progressContainer.nativeElement.removeEventListener('mouseup', this.boundOnDragEnd);
        this.progressContainer.nativeElement.removeEventListener('touchmove', this.boundOnDragging);
        this.progressContainer.nativeElement.removeEventListener('touchend', this.boundOnDragEnd);
        this.listenersAdded = false;
        document.removeEventListener('mousemove', this.boundOnDragging);
        document.removeEventListener('mouseup', this.boundOnDragEnd);
        document.removeEventListener('touchmove', this.boundOnDragging);
        document.removeEventListener('touchend', this.boundOnDragEnd);
      }

      this.removeSpriteListeners();
      this.removeHoverListeners();
      this.tooltipChange.emit({ time: '', chapterTitle: '', duration: '', show: false });

      this.dragEnded.emit({ wasPlaying: this.initPlayPause });
    }
  }

  updateProgress(event: MouseEvent | TouchEvent): void {
    this.ngZone.runOutsideAngular(() => {
      if (this.progressContainer) {
        const rect = this.progressContainer.nativeElement.getBoundingClientRect();
        let clientX: number | null = null;

        if (this.isTouchEvent(event)) {
          if (event.type === 'touchend' || event.type === 'touchcancel') {
            clientX = event.changedTouches?.[0]?.clientX ?? null;
          } else if (event.touches?.length) {
            clientX = event.touches[0].clientX;
          }
        } else if (event instanceof MouseEvent) {
          clientX = event.clientX;
        }

        if (clientX === null) return;

        const containerWidth = rect.width;
        const clickX = Math.max(0, Math.min(clientX - rect.left, containerWidth));
        const visualClickPercentage = clickX / containerWidth;

        let seekPercentage: number;
        if (this.showProgressBar) {
          if (visualClickPercentage < 0.7) {
            seekPercentage = (visualClickPercentage / 0.7) * 0.3;
          } else {
            seekPercentage = 0.3 + ((visualClickPercentage - 0.7) / 0.3) * 0.7;
          }
        } else {
          seekPercentage = visualClickPercentage;
        }

        const seekTime = seekPercentage * this.videoDurationSeconds;
        this.seekTo.emit(seekTime);

        let progressPercentage;
        if (this.showProgressBar) {
          progressPercentage = seekPercentage < 0.3
            ? (seekPercentage / 0.3) * 70
            : 70 + ((seekPercentage - 0.3) / 0.7) * 30;

          this.parallelProgress?.nativeElement?.style.setProperty('width', `${progressPercentage}%`);
        } else {
          progressPercentage = seekPercentage * 100;
          this.originalProgress?.nativeElement?.style.setProperty('width', `${progressPercentage}%`);
        }

        if (this.marker && this.progressContainer?.nativeElement) {
          const markerX = visualClickPercentage * containerWidth;
          this.marker.nativeElement.style.left = `${markerX - 7}px`;
        }

        this.applyChapterMasks(containerWidth, (progressPercentage / 100) * containerWidth);
      }
    });
  }

  updateDisplayedTime(time: number): void {
    this.displayedTime = this.formatDuration2(time);
    if (this.chaptersEnabled && this.chapters.length) {
      const active = [...this.chapters]
        .filter(c => this.chapterTimeToSeconds(c.time) <= time)
        .pop();
      this.displayedChapterTitle = active?.title || '';
    } else {
      this.displayedChapterTitle = '';
    }
    this.cdr.detectChanges();
  }

  formatDuration2(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    const mm = m.toString().padStart(2, '0');
    const ss = s.toString().padStart(2, '0');
    if (h > 0) return `${h.toString().padStart(2, '0')}:${mm}:${ss}`;
    return `${mm}:${ss}`;
  }

  updateThumbnail(clientX: number): void {
    // Precisa ser progressContainer (não progressContainerWrapper) — o wrapper
    // é mais largo (inclui o botão de velocidade + gap ao lado), então usar a
    // largura dele aqui subestimava a % e desincronizava o tempo exibido no
    // tooltip/contador em relação ao seekTime real usado pra tocar o vídeo.
    const progressBar = this.progressContainer?.nativeElement;
    if (!progressBar) return;

    const rect = progressBar.getBoundingClientRect();
    const boundedClientX = Math.max(rect.left, Math.min(clientX, rect.right));
    const relativeX = boundedClientX - rect.left;
    const spriteTime = (relativeX / rect.width) * this.videoDurationSeconds;

    this.updateDisplayedTime(spriteTime);
    this.tooltipChange.emit({
      time: this.displayedTime,
      chapterTitle: this.displayedChapterTitle,
      duration: this.formattedDuration,
      show: true
    });
  }

  removeSpriteListeners(): void {
    const wrapper = this.progressContainerWrapper?.nativeElement;
    if (!wrapper) return;

    const { touchMoveListener, mouseMoveListener, mouseLeaveListener, touchEndListener } = this.spriteListeners;

    if (touchMoveListener) wrapper.removeEventListener('touchmove', touchMoveListener);
    if (mouseMoveListener) wrapper.removeEventListener('mousemove', mouseMoveListener);
    if (mouseLeaveListener) wrapper.removeEventListener('mouseleave', mouseLeaveListener);
    if (touchEndListener) {
      wrapper.removeEventListener('touchend', touchEndListener);
      wrapper.removeEventListener('touchcancel', touchEndListener);
    }

    this.spriteListeners = {};
  }

  removeHoverListeners(): void {
    const progressBar = this.progressContainerWrapper?.nativeElement;

    if (!progressBar || !this.hoverListeners) return;

    const { mouseEnterListener, mouseMoveListener, mouseLeaveListener } = this.hoverListeners;

    if (mouseEnterListener) progressBar.removeEventListener('mouseenter', mouseEnterListener);
    if (mouseMoveListener) progressBar.removeEventListener('mousemove', mouseMoveListener);
    if (mouseLeaveListener) progressBar.removeEventListener('mouseleave', mouseLeaveListener);

    this.hoverListeners = {};
  }

  private removeAllListeners(): void {
    if (this.listenersAdded) {
      if (this.progressContainer) {
        this.progressContainer.nativeElement.removeEventListener('mousemove', this.boundOnDragging);
        this.progressContainer.nativeElement.removeEventListener('mouseup', this.boundOnDragEnd);
        this.progressContainer.nativeElement.removeEventListener('touchmove', this.boundOnDragging);
        this.progressContainer.nativeElement.removeEventListener('touchend', this.boundOnDragEnd);
      }
      document.removeEventListener('mousemove', this.boundOnDragging);
      document.removeEventListener('mouseup', this.boundOnDragEnd);
      document.removeEventListener('touchmove', this.boundOnDragging);
      document.removeEventListener('touchend', this.boundOnDragEnd);
    }
    this.removeSpriteListeners();
    this.removeHoverListeners();
  }

  getChapterMarkerLeft(chapterTime: string): number {
    if (!this.videoDurationSeconds) return 0;
    const pct = this.chapterTimeToSeconds(chapterTime) / this.videoDurationSeconds;
    if (this.showProgressBar) {
      if (pct < 0.3) return (pct / 0.3) * 70;
      return 70 + ((pct - 0.3) / 0.7) * 30;
    }
    return pct * 100;
  }

  private chapterTimeToSeconds(time: string): number {
    const parts = (time || '00:00:00').split(':').map(Number);
    return (parts[0] || 0) * 3600 + (parts[1] || 0) * 60 + (parts[2] || 0);
  }

  private computeChapterBoundariesPx(containerWidth: number): number[] {
    if (!this.chaptersEnabled || !this.chapters.length || !containerWidth) return [];
    return this.chapters.map(c => {
      const pct = this.getChapterMarkerLeft(c.time);
      return Math.max(0, Math.min(containerWidth, (pct / 100) * containerWidth));
    });
  }

  // Gera o gradiente do mask-image com "furos" transparentes nas fronteiras
  // de capítulo — cada furo tem chapterMaskHoleWidthPx de largura, terminando
  // exatamente na posição do capítulo (o buraco fica ANTES do início dele,
  // igual à referência em temp.js).
  private buildChapterMask(boundariesPx: number[], totalWidthPx: number): string {
    if (!totalWidthPx || totalWidthPx <= 0) {
      return 'linear-gradient(to right, #000 0%, #000 100%)';
    }

    const halfHoleWidthPx = this.chapterMaskHoleWidthPx / 2;
    const holes = boundariesPx
      .filter(px => px > 0 && px < totalWidthPx)
      .map(px => {
        // Furo centralizado na marca (metade antes, metade depois) — não
        // "adianta" visualmente o corte em relação ao tempo real do capítulo.
        const startPx = Math.max(0, px - halfHoleWidthPx);
        const endPx = Math.min(totalWidthPx, px + halfHoleWidthPx);
        return {
          start: (startPx / totalWidthPx) * 100,
          end: (endPx / totalWidthPx) * 100,
        };
      })
      .filter(h => h.end > h.start)
      .sort((a, b) => a.start - b.start);

    if (!holes.length) {
      return 'linear-gradient(to right, #000 0%, #000 100%)';
    }

    const stops: string[] = [];
    let last = 0;

    for (const hole of holes) {
      stops.push(`#000 ${last}%`);
      stops.push(`#000 ${hole.start}%`);
      stops.push(`transparent ${hole.start}%`);
      stops.push(`transparent ${hole.end}%`);
      last = hole.end;
    }

    stops.push(`#000 ${last}%`);
    stops.push('#000 100%');

    return `linear-gradient(to right, ${stops.join(', ')})`;
  }

  // Aplica o mask no trilho (::before de .ss-l-progress-container, sempre
  // furado nas fronteiras) e no preenchimento (só furado até onde o
  // progresso já passou — senão o furo "vazaria" mostrando o trilho por
  // baixo de uma parte ainda não tocada da barra).
  private applyChapterMasks(containerWidth: number, progressWidthPx: number): void {
    const trackEl = this.progressContainer?.nativeElement;
    if (!trackEl) return;

    if (!this.chaptersEnabled || !this.chapters.length) {
      trackEl.style.removeProperty('--ss-track-mask');
      this.originalProgress?.nativeElement.style.removeProperty('--ss-fill-mask');
      this.parallelProgress?.nativeElement.style.removeProperty('--ss-fill-mask');
      return;
    }

    const boundaries = this.computeChapterBoundariesPx(containerWidth);
    trackEl.style.setProperty('--ss-track-mask', this.buildChapterMask(boundaries, containerWidth));

    const fillBoundaries = boundaries.filter(px => px <= progressWidthPx);
    const fillMask = this.buildChapterMask(fillBoundaries, progressWidthPx);
    this.originalProgress?.nativeElement.style.setProperty('--ss-fill-mask', fillMask);
    this.parallelProgress?.nativeElement.style.setProperty('--ss-fill-mask', fillMask);
  }

  // Segmentos entre fronteiras de capítulo, usados só pro tooltip de hover
  // (achar qual capítulo corresponde ao trecho sob o cursor).
  private getChapterSegments(containerWidth: number): { start: number; end: number; title: string }[] {
    const items = this.chapters
      .map((c, i) => ({
        x: Math.max(0, Math.min(containerWidth, (this.getChapterMarkerLeft(c.time) / 100) * containerWidth)),
        title: c.title || `Capítulo ${i + 1}`,
      }))
      .sort((a, b) => a.x - b.x);

    if (!items.length) return [];

    if (items[0].x > 2) {
      items.unshift({ x: 0, title: items[0].title });
    }

    return items.map((item, index) => ({
      start: item.x,
      end: items[index + 1]?.x ?? containerWidth,
      title: item.title,
    }));
  }

  onProgressHover(event: MouseEvent): void {
    if (this.isDragging || !this.chaptersEnabled || !this.chapters.length) {
      this.segmentTooltipVisible = false;
      return;
    }

    const containerEl = this.progressContainer?.nativeElement;
    if (!containerEl) return;

    const rect = containerEl.getBoundingClientRect();
    const containerWidth = rect.width;
    if (!containerWidth) return;

    const x = event.clientX - rect.left;
    const segment = this.getChapterSegments(containerWidth).find(s => x >= s.start && x < s.end);

    if (!segment) {
      this.segmentTooltipVisible = false;
      return;
    }

    this.segmentTooltipText = segment.title;
    this.segmentTooltipX = event.clientX;
    this.segmentTooltipY = event.clientY;
    this.segmentTooltipVisible = true;
  }

  onProgressHoverEnd(): void {
    this.segmentTooltipVisible = false;
  }

  private getClientXFromEvent(event: MouseEvent | TouchEvent): number | null {
    if (event instanceof MouseEvent) {
      return event.clientX;
    } else if (this.isTouchEvent(event)) {
      let clientX: number | null = null;
      if (event.type === 'touchend' || event.type === 'touchcancel') {
        if (event.changedTouches && event.changedTouches.length > 0) {
          clientX = event.changedTouches[0].clientX;
        }
      } else if (event.touches && event.touches.length > 0) {
        clientX = event.touches[0].clientX;
      }
      return clientX;
    }
    return null;
  }

  private isTouchEvent(event: MouseEvent | TouchEvent): event is TouchEvent {
    return event.type.startsWith('touch');
  }
}
